import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Chip, Typography, IconButton, Button, Snackbar, Alert } from '@mui/material';
import { useOrder } from '../../state/OrderContext';
import { useMenu } from '../../state/MenuContext';
import { PRINT_SERVER } from '../../data/printServer';
import { formatPrice } from '../../utils/format';

interface OrderScreenProps {
  tableNumber: number;
  onOpenBill: () => void;
}

const OrderScreen: React.FC<OrderScreenProps> = ({ tableNumber, onOpenBill }) => {
  const { t, i18n } = useTranslation();
  const { state, addItem, changeQty, sendToKitchen } = useOrder();
  const { tabs, getMenuItem } = useMenu();
  const visibleTabs = useMemo(() => tabs.filter((tab) => !tab.archived), [tabs]);
  const [activeCategory, setActiveCategory] = useState<string>('');
  const [sending, setSending] = useState(false);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  useEffect(() => {
    if (!activeCategory && visibleTabs.length > 0) {
      setActiveCategory(visibleTabs[0].id);
    }
  }, [visibleTabs, activeCategory]);

  const order = state.tables[tableNumber];
  const items = order?.items ?? [];
  const total = items.reduce((sum, line) => {
    const menuItem = getMenuItem(line.menuItemId);
    return sum + (menuItem ? menuItem.price * line.quantity : 0);
  }, 0);
  const pendingToSend = items.filter((line) => !line.sent);
  const activeItems = (visibleTabs.find((c) => c.id === activeCategory)?.items ?? []).filter(
    (item) => !item.archived
  );

  const handleSendToKitchen = async (): Promise<void> => {
    if (pendingToSend.length === 0) return;
    setSending(true);
    const printItems = pendingToSend.map((line) => ({
      name: getMenuItem(line.menuItemId)?.name ?? line.menuItemId,
      quantity: line.quantity,
      categoryId: tabs.find(t => t.items.some(i => i.id === line.menuItemId))?.id ?? null,
    }));
    try {
      const r = await fetch(`${PRINT_SERVER}/print`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tableNumber, items: printItems }),
      });
      const json = await r.json();
      if (!r.ok) throw new Error(json.error || 'Print failed');
      sendToKitchen(tableNumber);
      setSnackbar({ open: true, message: t('order.orderSent'), severity: 'success' });
    } catch (e: any) {
      setSnackbar({ open: true, message: t('order.sendFailed', { message: e.message }), severity: 'error' });
    } finally {
      setSending(false);
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minHeight: 0 }}>
        <Box
          sx={{
            display: 'flex',
            gap: 1,
            overflowX: 'auto',
            p: 1.5,
            backgroundColor: '#fff',
            borderBottom: '1px solid #F0F0F0',
            flexShrink: 0,
          }}
        >
          {visibleTabs.map((category) => (
            <Chip
              key={category.id}
              label={category.name}
              onClick={() => setActiveCategory(category.id)}
              sx={{
                backgroundColor: activeCategory === category.id ? 'primary.main' : '#F0F0F0',
                color: activeCategory === category.id ? '#fff' : '#616161',
                fontWeight: 600,
                flexShrink: 0,
              }}
            />
          ))}
        </Box>

        <Box
          sx={{
            flex: 1,
            overflowY: 'auto',
            p: 1.25,
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))',
            gap: 1,
            alignContent: 'start',
          }}
        >
          {activeItems.map((menuItem) => {
            const line = items.find((i) => i.menuItemId === menuItem.id);
            const qty = line?.quantity ?? 0;
            const unavailable = !!state.unavailableItemIds[menuItem.id];
            return (
              <Box
                key={menuItem.id}
                onClick={() => !unavailable && addItem(tableNumber, menuItem.id)}
                sx={{
                  backgroundColor: menuItem.color,
                  borderRadius: '14px',
                  p: '10px 6px',
                  cursor: unavailable ? 'default' : 'pointer',
                  opacity: unavailable ? 0.35 : 1,
                  textAlign: 'center',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 0.5,
                  position: 'relative',
                  boxShadow: qty > 0 ? '0 0 0 3px #fff, 0 4px 12px rgba(0,0,0,0.25)' : '0 2px 6px rgba(0,0,0,0.18)',
                  userSelect: 'none',
                }}
              >
                <Typography sx={{ fontWeight: 600, fontSize: '0.75rem', lineHeight: 1.3, color: '#fff' }}>
                  {menuItem.name}
                </Typography>
                <Typography sx={{ fontWeight: 700, fontSize: '0.8rem', color: 'rgba(255,255,255,0.85)' }}>
                  {formatPrice(menuItem.price, i18n.language)}
                </Typography>
                {qty > 0 && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: -7,
                      right: -7,
                      backgroundColor: '#fff',
                      color: menuItem.color,
                      borderRadius: '10px',
                      fontWeight: 800,
                      fontSize: '0.65rem',
                      px: 0.75,
                      py: 0.1,
                      minWidth: 20,
                      textAlign: 'center',
                      boxShadow: '0 2px 5px rgba(0,0,0,0.2)',
                    }}
                  >
                    {qty}
                  </Box>
                )}
              </Box>
            );
          })}
        </Box>
      </Box>

      <Box sx={{ height: '1px', backgroundColor: '#E0E0E0', flexShrink: 0 }} />

      <Box
        sx={{
          flex: '0 0 auto',
          maxHeight: '46%',
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: '#FAFAFA',
          minHeight: 160,
        }}
      >
        <Box sx={{ px: 1.75, pt: 1.25, pb: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
          <Typography sx={{ fontWeight: 700, fontSize: '0.8rem', color: '#424242' }}>{t('order.basket')}</Typography>
          <Typography sx={{ fontWeight: 800, fontSize: '1rem', color: 'primary.main' }}>
            {formatPrice(total, i18n.language)}
          </Typography>
        </Box>

        <Box sx={{ flex: 1, overflowY: 'auto', px: 1.25 }}>
          {items.length === 0 ? (
            <Box sx={{ textAlign: 'center', p: 2.5, color: '#BDBDBD' }}>
              <Typography sx={{ fontSize: '0.8rem' }}>{t('order.emptyBasket')}</Typography>
            </Box>
          ) : (
            items.map((line) => {
              const menuItem = getMenuItem(line.menuItemId);
              if (!menuItem) return null;
              return (
                <Box
                  key={line.menuItemId}
                  sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.75, borderBottom: '1px solid #EEEEEE' }}
                >
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography noWrap sx={{ fontWeight: 500, fontSize: '0.85rem', color: '#212121' }}>
                      {menuItem.name}
                    </Typography>
                    <Typography sx={{ fontSize: '0.7rem', color: '#9E9E9E' }}>
                      {formatPrice(menuItem.price * line.quantity, i18n.language)}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexShrink: 0 }}>
                    <IconButton
                      size="small"
                      onClick={() => changeQty(tableNumber, line.menuItemId, -1)}
                      sx={{ width: 28, height: 28, border: '1.5px solid', borderColor: 'primary.main', color: 'primary.main' }}
                    >
                      −
                    </IconButton>
                    <Typography sx={{ fontWeight: 700, fontSize: '0.85rem', minWidth: 18, textAlign: 'center' }}>
                      {line.quantity}
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={() => changeQty(tableNumber, line.menuItemId, 1)}
                      sx={{ width: 28, height: 28, backgroundColor: 'primary.main', color: '#fff' }}
                    >
                      +
                    </IconButton>
                  </Box>
                </Box>
              );
            })
          )}
        </Box>

        <Box sx={{ p: 1.25, display: 'flex', gap: 1, flexShrink: 0 }}>
          <Button
            variant="contained"
            fullWidth
            disabled={pendingToSend.length === 0 || sending}
            onClick={handleSendToKitchen}
            sx={{ flex: 2 }}
          >
            {sending ? t('order.sending') : t('order.sendToKitchen')}
          </Button>
          <Button variant="contained" color="secondary" disabled={items.length === 0} onClick={onOpenBill} sx={{ flex: 1 }}>
            {t('order.openBill')}
          </Button>
        </Box>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default OrderScreen;
